/**
 * Global Quote Version Configuration
 * 
 * Centralized management of quote versioning across the application.
 * This file controls which version new quotes are tagged with.
 */

// Current version for all new quotes - increment this number for major changes
export const CURRENT_QUOTE_VERSION = 2;

// Version type definitions
export type LegacyQuoteVersion = '1.0.0'; // Original string-based version
export type NumericQuoteVersion = number; // New number-based versions (2, 3, 4, etc.)
export type ApiQuoteVersion = 'v1' | 'v2'; // API-compatible versions
export type QuoteVersionValue = LegacyQuoteVersion | NumericQuoteVersion | ApiQuoteVersion;

// Import StoredQuoteVersion from API for consistency
export type { StoredQuoteVersion } from '@/services/quoteApi';

// Numeric version type (simplified)
export type QuoteVersionNumber = 1 | 2;

/**
 * Get the current quote version for new quote creation
 * This function determines what version to assign to newly created quotes
 */
export const getCurrentQuoteVersion = (): NumericQuoteVersion => {
  return CURRENT_QUOTE_VERSION;
};

/**
 * Convert any version value to numeric format (1 or 2)
 * All string versions (legacy "1.0.0", "v1", "v2") → 1
 * All numeric versions → preserve as-is (1→1, 2→2, ≥2→2)
 */
export const mapVersionToNumber = (version: QuoteVersionValue | undefined): QuoteVersionNumber => {
  // Handle numeric versions - preserve as-is, ensure minimum of 1
  if (typeof version === 'number') {
    return version >= 2 ? 2 : 1;
  }
  
  // All string versions (legacy "1.0.0", "v1", "v2") → 1
  if (typeof version === 'string') {
    return 1;
  }
  
  // Fallback for undefined/null versions (legacy quotes) → 1
  return 1;
};

/**
 * Determine if a quote version represents a kit-based (version 2) quote
 */
export const isKitBasedVersion = (version: QuoteVersionValue | undefined): boolean => {
  return mapVersionToNumber(version) === 2;
};

/**
 * Determine if a quote version represents an individual product (version 1) quote
 */
export const isIndividualProductVersion = (version: QuoteVersionValue | undefined): boolean => {
  return mapVersionToNumber(version) === 1;
};

/**
 * Get version info for logging and debugging
 */
export const getVersionInfo = (version: QuoteVersionValue | undefined) => {
  const numericVersion = mapVersionToNumber(version);
  const isLegacy = typeof version === 'string';
  const isNumeric = typeof version === 'number';
  
  return {
    originalValue: version,
    numericVersion,
    isLegacy,
    isNumeric,
    isKitBased: numericVersion === 2,
    description: isLegacy ? 'Legacy string version (mapped to 1)' : 
                isNumeric ? `Numeric version ${version}` : 
                'Unknown/undefined version (mapped to 1)'
  };
};